import React, { createContext, useContext, useState, useCallback } from "react";
import type { User } from "../services/auth";
import newsService, { type NewsArticle } from "../services/newsService";

// Article interface
interface Article {
  id: string;
  title: string;
  content: string;
  summary: string;
  slug: string;
  author: User;
  category: string;
  tags: string[];
  imageUrl?: string;
  publishedAt: string;
  updatedAt: string;
  status: "draft" | "published" | "archived";
  likes: number;
  comments: number;
  views: number;
}

// Map newsService Article to local Article interface
const mapNewsArticle = (article: NewsArticle): Article => ({
  id: article.id,
  title: article.title,
  content: article.content,
  summary: article.excerpt,
  slug: article.slug,
  author: {
    id: article.author?.id || "",
    email: "",
    username: article.author?.name || "Unknown",
    displayName: article.author?.name || "Unknown",
    role: "user",
  } as User,
  category: article.categories?.[0]?.name || "Uncategorized",
  tags: article.tags?.map((t) => t.name) || [],
  imageUrl: article.imageUrl || article.coverImage,
  publishedAt: article.publishedAt || article.createdAt,
  updatedAt: article.updatedAt,
  status: (article.status as any) || "published",
  likes: 0,
  comments: article.commentCount || 0,
  views: 0,
});

interface NewsContextState {
  // Article data
  latestArticles: Article[];
  featuredArticles: Article[];
  popularArticles: Article[];

  // Loading states
  isLatestLoading: boolean;
  isFeaturedLoading: boolean;
  isPopularLoading: boolean;
  isLoadingMore: boolean;

  // Error states
  latestError: string | null;
  featuredError: string | null;
  popularError: string | null;

  // Article actions
  getArticle: (slug: string) => Promise<Article>;
  getArticleComments: (articleId: string) => Promise<any[]>;
  likeArticle: (articleId: string) => Promise<void>;
  unlikeArticle: (articleId: string) => Promise<void>;

  // Refresh actions
  refreshLatest: () => Promise<void>;
  refreshFeatured: () => Promise<void>;
  refreshPopular: () => Promise<void>;
  loadMoreLatest: () => Promise<void>;
}

const NewsContext = createContext<NewsContextState | undefined>(undefined);

export const NewsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Article data states
  const [latestArticles, setLatestArticles] = useState<Article[]>([]);
  const [featuredArticles, setFeaturedArticles] = useState<Article[]>([]);
  const [popularArticles, setPopularArticles] = useState<Article[]>([]);

  // Loading states
  const [isLatestLoading, setIsLatestLoading] = useState(true);
  const [isFeaturedLoading, setIsFeaturedLoading] = useState(true);
  const [isPopularLoading, setIsPopularLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Error states
  const [latestError, setLatestError] = useState<string | null>(null);
  const [featuredError, setFeaturedError] = useState<string | null>(null);
  const [popularError, setPopularError] = useState<string | null>(null);

  // Load latest articles
  const loadLatestArticles = useCallback(async () => {
    setIsLatestLoading(true);
    setLatestError(null);

    try {
      const articles = await newsService.getLatestNews(10);
      setLatestArticles(articles.map(mapNewsArticle));
      setPage(1);
      setHasMore(articles.length >= 10);
    } catch (err) {
      setLatestError(
        err instanceof Error ? err.message : "Failed to load latest articles",
      );
      console.error("Failed to load latest articles:", err);
    } finally {
      setIsLatestLoading(false);
    }
  }, []);

  // Load more latest articles
  const loadMoreLatestArticles = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    setIsLoadingMore(true);
    const nextPage = page + 1;

    try {
      // The news service might not support page parameter directly via getLatestNews,
      // but getNews does. We'll use getNews with pagination.
      const response = await newsService.getNews({
        page: nextPage,
        limit: 10,
        sortBy: "publishedAt",
        sortOrder: "desc",
      });

      const newArticles = response?.data || response?.articles || [];
      const mappedNewArticles = Array.isArray(newArticles)
        ? newArticles.map(mapNewsArticle)
        : [];

      if (mappedNewArticles.length > 0) {
        setLatestArticles((prev) => [...prev, ...mappedNewArticles]);
        setPage(nextPage);
        setHasMore(mappedNewArticles.length >= 10);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error("Failed to load more articles:", err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [page, hasMore, isLoadingMore]);

  // Load featured articles
  const loadFeaturedArticles = useCallback(async () => {
    setIsFeaturedLoading(true);
    setFeaturedError(null);

    try {
      const response = await newsService.getNews({ featured: true, limit: 5 });
      const articles = response?.data || response || [];
      // @ts-ignore - Handle difference in backend vs frontend structure safely
      setFeaturedArticles(
        Array.isArray(articles) ? articles.map(mapNewsArticle) : [],
      );
    } catch (err) {
      setFeaturedError(
        err instanceof Error ? err.message : "Failed to load featured articles",
      );
      console.error("Failed to load featured articles:", err);
    } finally {
      setIsFeaturedLoading(false);
    }
  }, []);

  // Load popular articles
  const loadPopularArticles = useCallback(async () => {
    setIsPopularLoading(true);
    setPopularError(null);

    try {
      const response = await newsService.getNews({
        sortBy: "publishedAt",
        sortOrder: "desc",
        limit: 10,
      });
      const articles = response?.data || response || [];
      // @ts-ignore
      setPopularArticles(
        Array.isArray(articles) ? articles.map(mapNewsArticle) : [],
      );
    } catch (err) {
      setPopularError(
        err instanceof Error ? err.message : "Failed to load popular articles",
      );
      console.error("Failed to load popular articles:", err);
    } finally {
      setIsPopularLoading(false);
    }
  }, []);

  // Initial data load - Removed to prevent redundant fetching on every page navigation
  // useEffect(() => {
  //   loadLatestArticles();
  //   loadFeaturedArticles();
  //   loadPopularArticles();
  // }, [loadLatestArticles, loadFeaturedArticles, loadPopularArticles]);

  // Get single article
  const getArticle = async (slug: string): Promise<Article> => {
    try {
      const article = await newsService.getNewsBySlug(slug);
      return mapNewsArticle(article);
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load article",
      );
    }
  };

  // Get article comments - placeholder since newsService doesn't have this yet
  const getArticleComments = async (_articleId: string): Promise<any[]> => {
    try {
      return [];
    } catch (err) {
      throw new Error(
        err instanceof Error ? err.message : "Failed to load comments",
      );
    }
  };

  // Like/unlike - placeholder since newsService doesn't have this yet
  const likeArticle = async (_articleId: string): Promise<void> => {
    console.log("Like article not implemented");
  };

  const unlikeArticle = async (_articleId: string): Promise<void> => {
    console.log("Unlike article not implemented");
  };

  const value: NewsContextState = {
    // Article data
    latestArticles,
    featuredArticles,
    popularArticles,

    // Loading states
    isLatestLoading,
    isFeaturedLoading,
    isPopularLoading,
    isLoadingMore,

    // Error states
    latestError,
    featuredError,
    popularError,

    // Article actions
    getArticle,
    getArticleComments,
    likeArticle,
    unlikeArticle,

    // Refresh actions
    refreshLatest: loadLatestArticles,
    refreshFeatured: loadFeaturedArticles,
    refreshPopular: loadPopularArticles,
    loadMoreLatest: loadMoreLatestArticles,
  };

  return <NewsContext.Provider value={value}>{children}</NewsContext.Provider>;
};

export const useNews = () => {
  const context = useContext(NewsContext);
  if (context === undefined) {
    throw new Error("useNews must be used within a NewsProvider");
  }
  return context;
};

export default NewsContext;
